import React, { useEffect, useRef, useState } from 'react';
import {
  CombineLinkResponse,
  CombineSuggestion,
  CombineSuggestionsResponse,
  MarkShippedPayload,
  Order,
  fetchCombineSuggestions,
  linkOrderCombine,
  markOrderShipped,
  unlinkOrderCombine,
} from '../../api/orders';
import { colors, spacing, typography, typographyFlat, borderRadius } from '../../styles/tokens';
import { formatInstant } from '../../utils/datetime';
import { fulfillmentStatusLabel } from '../../utils/orderStatus';

const FINISHED_FULFILLMENT = new Set(['shipped', 'delivered', 'cancelled']);

export interface CombinePackageClient {
  fetchSuggestions: (orderId: string) => Promise<CombineSuggestionsResponse>;
  link: (orderId: string, partnerOrderId: string) => Promise<CombineLinkResponse>;
  unlink: (orderId: string) => Promise<CombineLinkResponse>;
  markShipped: (orderId: string, payload: MarkShippedPayload) => Promise<Order>;
}

const defaultClient: CombinePackageClient = {
  fetchSuggestions: fetchCombineSuggestions,
  link: linkOrderCombine,
  unlink: unlinkOrderCombine,
  markShipped: markOrderShipped,
};

function clearedLink(orderId: string): CombineLinkResponse {
  return {
    order: {
      order_id: orderId,
      combined_with_order_id: null,
      combined_with_label: null,
    },
    partner: null,
  };
}

interface CombinePackageSectionProps {
  order: Order;
  onOrderUpdated?: (order: Order) => void;
  onOpenPartner?: (partnerOrderId: string) => void;
  onActiveChange?: (active: boolean) => void;
  /** Test and sample-data seam. Production uses the orders API client. */
  client?: CombinePackageClient;
}

function apiErrorMessage(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } } | null)?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object' && 'msg' in item) {
          return String((item as { msg: unknown }).msg);
        }
        return '';
      })
      .filter(Boolean);
    if (parts.length > 0) {
      return parts.join('; ');
    }
  }
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return fallback;
}

function formatMoney(amount: string | number | null | undefined): string {
  if (amount === undefined || amount === null || amount === '') {
    return '—';
  }
  const num = typeof amount === 'number' ? amount : parseFloat(amount);
  if (Number.isNaN(num)) {
    return '—';
  }
  return `$${num.toFixed(2)}`;
}

function partnerLabel(order: Order): string {
  return order.combined_with_label || order.combined_with_order_id || 'partner order';
}

function suggestionLabel(suggestion: CombineSuggestion): string {
  return suggestion.external_label || suggestion.order_id;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={styles.fieldLabel}>{label}</div>
      <div style={styles.fieldValue}>{children}</div>
    </div>
  );
}

export const CombinePackageSection: React.FC<CombinePackageSectionProps> = ({
  order,
  onOrderUpdated,
  onOpenPartner,
  onActiveChange,
  client = defaultClient,
}) => {
  const [suggestions, setSuggestions] = useState<CombineSuggestion[] | null>(null);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [unlinkOpen, setUnlinkOpen] = useState(false);
  const [unlinkChecked, setUnlinkChecked] = useState(false);
  const [unlinkBusy, setUnlinkBusy] = useState(false);
  const [unlinkError, setUnlinkError] = useState<string | null>(null);
  const [tracking, setTracking] = useState('');
  const [carrier, setCarrier] = useState('');
  const [shipBusy, setShipBusy] = useState(false);
  const [shipError, setShipError] = useState<string | null>(null);
  const [shipResult, setShipResult] = useState<{
    order: Order;
    tracking?: string;
    carrier?: string;
  } | null>(null);
  const requestRef = useRef(0);
  const [trackedOrderId, setTrackedOrderId] = useState(order.id);
  if (trackedOrderId !== order.id) {
    setTrackedOrderId(order.id);
    requestRef.current += 1;
    setSuggestions(null);
    setSuggestionsLoading(false);
    setSuggestionsError(null);
    setSelectedId(null);
    setConfirmChecked(false);
    setLinkBusy(false);
    setLinkError(null);
    setUnlinkOpen(false);
    setUnlinkChecked(false);
    setUnlinkBusy(false);
    setUnlinkError(null);
    setTracking('');
    setCarrier('');
    setShipBusy(false);
    setShipError(null);
    setShipResult(null);
  }

  const linkedId = order.combined_with_order_id || null;
  const selected = suggestions?.find((item) => item.order_id === selectedId) ?? null;
  const panelActive = suggestions !== null || Boolean(selectedId) || unlinkOpen;
  const canMarkShipped = !FINISHED_FULFILLMENT.has((order.fulfillment_status || '').toLowerCase());

  useEffect(() => {
    onActiveChange?.(panelActive);
  }, [panelActive, onActiveChange]);

  useEffect(() => {
    return () => onActiveChange?.(false);
  }, [onActiveChange]);

  const loadSuggestions = async () => {
    const requestId = ++requestRef.current;
    setSuggestionsLoading(true);
    setSuggestionsError(null);
    setLinkError(null);
    try {
      const data = await client.fetchSuggestions(order.id);
      if (requestId !== requestRef.current) return;
      const list = (data.suggestions ?? []).filter((item) => item.order_id && item.order_id !== order.id);
      setSuggestions(list);
      setSelectedId((current) => (current && list.some((item) => item.order_id === current) ? current : null));
      setConfirmChecked(false);
    } catch (err) {
      if (requestId !== requestRef.current) return;
      setSuggestionsError(apiErrorMessage(err, 'Failed to load partner suggestions'));
    } finally {
      if (requestId === requestRef.current) {
        setSuggestionsLoading(false);
      }
    }
  };

  const confirmLink = async () => {
    if (!selected || !confirmChecked || linkBusy) return;
    setLinkBusy(true);
    setLinkError(null);
    try {
      const returned = await client.link(order.id, selected.order_id);
      const side = returned?.order;
      const next: Order = {
        ...order,
        combined_with_order_id: side?.combined_with_order_id ?? selected.order_id,
        combined_with_label: side?.combined_with_label ?? suggestionLabel(selected),
      };
      setSuggestions(null);
      setSelectedId(null);
      setConfirmChecked(false);
      onOrderUpdated?.(next);
    } catch (err) {
      setLinkError(apiErrorMessage(err, 'Failed to link orders'));
    } finally {
      setLinkBusy(false);
    }
  };

  const confirmUnlink = async () => {
    if (!linkedId || !unlinkChecked || unlinkBusy) return;
    setUnlinkBusy(true);
    setUnlinkError(null);
    try {
      const returned = await client.unlink(order.id);
      const side = returned?.order ?? clearedLink(order.id).order;
      const next: Order = {
        ...order,
        combined_with_order_id: side.combined_with_order_id ?? null,
        combined_with_label: side.combined_with_label ?? null,
      };
      setUnlinkOpen(false);
      setUnlinkChecked(false);
      onOrderUpdated?.(next);
    } catch (err) {
      setUnlinkError(apiErrorMessage(err, 'Failed to unlink orders'));
    } finally {
      setUnlinkBusy(false);
    }
  };

  const submitShipped = async () => {
    if (shipBusy || !canMarkShipped) return;
    const payload: MarkShippedPayload = {};
    const trackingValue = tracking.trim();
    const carrierValue = carrier.trim();
    if (trackingValue) payload.tracking_number = trackingValue;
    if (carrierValue) payload.carrier = carrierValue;
    setShipBusy(true);
    setShipError(null);
    try {
      const returned = await client.markShipped(order.id, payload);
      const next: Order = {
        ...order,
        ...(returned ?? {}),
        id: returned?.id || order.id,
      };
      setShipResult({
        order: next,
        tracking: trackingValue || undefined,
        carrier: carrierValue || undefined,
      });
      setTracking('');
      setCarrier('');
      onOrderUpdated?.(next);
    } catch (err) {
      setShipError(apiErrorMessage(err, 'Failed to mark shipped'));
    } finally {
      setShipBusy(false);
    }
  };

  const currentLabel = order.display_id || order.label || order.external_id;

  return (
    <div style={styles.wrap}>
      <section aria-label="Combine into package" style={styles.card}>
        <h3 style={styles.heading}>{linkedId ? 'Combined package' : 'Combine into package'}</h3>
        {linkedId ? (
          <div>
            <p style={styles.help}>
              This order is linked to a partner and can ship in the same package.
            </p>
            <div style={styles.linkedBox}>
              <Field label="Partner label">
                {onOpenPartner ? (
                  <button
                    type="button"
                    onClick={() => onOpenPartner(linkedId)}
                    style={styles.linkButton}
                  >
                    Open partner {partnerLabel(order)}
                  </button>
                ) : (
                  partnerLabel(order)
                )}
              </Field>
              <Field label="Partner order id">
                <span style={styles.mono}>{linkedId}</span>
              </Field>
            </div>
            {!unlinkOpen ? (
              <button
                type="button"
                onClick={() => {
                  setUnlinkOpen(true);
                  setUnlinkChecked(false);
                  setUnlinkError(null);
                }}
                style={styles.secondaryButton}
              >
                Unlink
              </button>
            ) : (
              <div role="dialog" aria-labelledby="unlink-confirm-title" style={styles.confirmBox}>
                <h4 id="unlink-confirm-title" style={styles.confirmTitle}>
                  Remove package link
                </h4>
                <p style={styles.help}>
                  Unlink {currentLabel} from {partnerLabel(order)}. Both orders stay in SyncBase; only the package association is removed.
                </p>
                <label style={styles.checkLabel}>
                  <input
                    type="checkbox"
                    checked={unlinkChecked}
                    onChange={(event) => setUnlinkChecked(event.target.checked)}
                  />
                  I confirm unlinking this package
                </label>
                {unlinkError && <div style={styles.error}>{unlinkError}</div>}
                <div style={styles.row}>
                  <button
                    type="button"
                    disabled={!unlinkChecked || unlinkBusy}
                    onClick={() => { void confirmUnlink(); }}
                    style={{
                      ...styles.primaryButton,
                      ...((!unlinkChecked || unlinkBusy) ? styles.buttonDisabled : {}),
                    }}
                  >
                    {unlinkBusy ? 'Unlinking…' : 'Confirm unlink'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setUnlinkOpen(false);
                      setUnlinkChecked(false);
                      setUnlinkError(null);
                    }}
                    style={styles.secondaryButton}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div>
            <p style={styles.help}>
              Suggestions only. Nothing is linked until you confirm the partner.
            </p>
            <button
              type="button"
              disabled={suggestionsLoading}
              onClick={() => { void loadSuggestions(); }}
              style={{
                ...styles.primaryButton,
                ...(suggestionsLoading ? styles.buttonDisabled : {}),
              }}
            >
              {suggestionsLoading
                ? 'Loading suggestions…'
                : suggestions
                  ? 'Refresh suggestions'
                  : 'Combine into package'}
            </button>
            {suggestionsError && <div style={styles.error}>{suggestionsError}</div>}
            {suggestions && suggestions.length === 0 && (
              <p style={styles.help}>No partner orders suggested.</p>
            )}
            {suggestions && suggestions.length > 0 && (
              <div style={styles.suggestionList}>
                {suggestions.map((suggestion) => {
                  const label = suggestionLabel(suggestion);
                  const isSelected = suggestion.order_id === selectedId;
                  return (
                    <article
                      key={suggestion.order_id}
                      aria-label={`Suggested partner ${label}`}
                      style={{
                        ...styles.suggestion,
                        ...(isSelected ? styles.suggestionSelected : {}),
                      }}
                    >
                      <div style={styles.suggestionGrid}>
                        <Field label="Label">{label}</Field>
                        <Field label="Buyer">{suggestion.buyer_name || '—'}</Field>
                        <Field label="Status">
                          {fulfillmentStatusLabel(suggestion.status)}
                          {' · '}
                          {fulfillmentStatusLabel(suggestion.commercial_status)}
                        </Field>
                        <Field label="Total">{formatMoney(suggestion.total)}</Field>
                        <Field label="Channel">{suggestion.channel || '—'}</Field>
                        <Field label="Opened">{formatInstant(suggestion.created_at)}</Field>
                      </div>
                      <Field label="Reason">{suggestion.reason || '—'}</Field>
                      <button
                        type="button"
                        aria-pressed={isSelected}
                        onClick={() => {
                          setSelectedId(suggestion.order_id);
                          setConfirmChecked(false);
                          setLinkError(null);
                        }}
                        style={styles.secondaryButton}
                      >
                        {isSelected ? `Selected ${label}` : `Select ${label}`}
                      </button>
                    </article>
                  );
                })}
              </div>
            )}
            {selected && (
              <div role="dialog" aria-labelledby="combine-confirm-title" style={styles.confirmBox}>
                <h4 id="combine-confirm-title" style={styles.confirmTitle}>
                  Confirm package link
                </h4>
                <p style={styles.help}>
                  Link {currentLabel} with {suggestionLabel(selected)} ({selected.buyer_name || 'unknown buyer'})?
                  This records the association in SyncBase. It does not ship either order.
                </p>
                <label style={styles.checkLabel}>
                  <input
                    type="checkbox"
                    checked={confirmChecked}
                    onChange={(event) => setConfirmChecked(event.target.checked)}
                  />
                  I confirm these orders belong in the same package
                </label>
                {linkError && <div style={styles.error}>{linkError}</div>}
                <div style={styles.row}>
                  <button
                    type="button"
                    disabled={!confirmChecked || linkBusy}
                    onClick={() => { void confirmLink(); }}
                    style={{
                      ...styles.primaryButton,
                      ...((!confirmChecked || linkBusy) ? styles.buttonDisabled : {}),
                    }}
                  >
                    {linkBusy ? 'Linking…' : 'Confirm link'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedId(null);
                      setConfirmChecked(false);
                      setLinkError(null);
                    }}
                    style={styles.secondaryButton}
                  >
                    Back
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {(canMarkShipped || shipResult) && (
        <section aria-label="Mark shipped" style={styles.card}>
          <h3 style={styles.heading}>Mark shipped</h3>
          <p style={styles.help}>
            Records shipped in SyncBase. Optional tracking number and carrier. This does not call Manapool.
          </p>
          {canMarkShipped && (
            <div style={styles.shipForm}>
              <label style={styles.inputLabel}>
                Tracking number
                <input
                  value={tracking}
                  onChange={(event) => setTracking(event.target.value)}
                  placeholder="Optional"
                  style={styles.input}
                />
              </label>
              <label style={styles.inputLabel}>
                Carrier
                <input
                  value={carrier}
                  onChange={(event) => setCarrier(event.target.value)}
                  placeholder="Optional"
                  style={styles.input}
                />
              </label>
              {shipError && <div style={styles.error}>{shipError}</div>}
              <button
                type="button"
                disabled={shipBusy}
                onClick={() => { void submitShipped(); }}
                style={{
                  ...styles.primaryButton,
                  ...(shipBusy ? styles.buttonDisabled : {}),
                }}
              >
                {shipBusy ? 'Marking shipped…' : 'Mark shipped'}
              </button>
            </div>
          )}
          {shipResult && (
            <div role="status" aria-label="Ship result" style={styles.resultBox}>
              <div style={styles.resultTitle}>Ship result</div>
              <Field label="Fulfillment">
                {fulfillmentStatusLabel(shipResult.order.fulfillment_status)}
              </Field>
              <Field label="Commercial">
                {fulfillmentStatusLabel(shipResult.order.commercial_status)}
              </Field>
              {(shipResult.order.commercial_status || '').toLowerCase() === 'partially_refunded' && (
                <p style={styles.preserved}>Commercial status stayed partially refunded.</p>
              )}
              {(shipResult.carrier || shipResult.tracking) && (
                <Field label="Tracking">
                  {[shipResult.carrier, shipResult.tracking].filter(Boolean).join(' · ')}
                </Field>
              )}
            </div>
          )}
        </section>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  wrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.md,
    marginBottom: spacing.lg,
  },
  card: {
    backgroundColor: colors.backgroundAlt,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
  },
  heading: {
    fontSize: typography.h4.fontSize,
    fontWeight: typography.h4.fontWeight,
    color: colors.text,
    margin: 0,
    marginBottom: spacing.sm,
  },
  help: {
    fontSize: typography.small.fontSize,
    color: colors.textSecondary,
    margin: 0,
    marginBottom: spacing.sm,
    lineHeight: 1.45,
  },
  fieldLabel: {
    fontSize: typography.caption.fontSize,
    color: colors.textSecondary,
    marginBottom: spacing.xs,
  },
  fieldValue: {
    fontSize: typography.small.fontSize,
    color: colors.text,
  },
  suggestionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.sm,
    marginTop: spacing.md,
  },
  suggestion: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    padding: spacing.md,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.sm,
    backgroundColor: colors.white,
  },
  suggestionSelected: {
    borderColor: colors.primary,
  },
  suggestionGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: spacing.sm,
  },
  linkedBox: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  confirmBox: {
    marginTop: spacing.md,
    padding: spacing.md,
    border: `1px solid ${colors.primary}`,
    borderRadius: borderRadius.md,
    backgroundColor: `${colors.primary}0F`,
  },
  confirmTitle: {
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: colors.text,
    margin: 0,
    marginBottom: spacing.xs,
  },
  checkLabel: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: spacing.sm,
    fontSize: typography.small.fontSize,
    color: colors.text,
    marginBottom: spacing.sm,
  },
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  primaryButton: {
    padding: `${spacing.sm} ${spacing.md}`,
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: borderRadius.md,
    cursor: 'pointer',
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.medium,
  },
  secondaryButton: {
    padding: `${spacing.sm} ${spacing.md}`,
    backgroundColor: colors.backgroundAlt,
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    cursor: 'pointer',
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.medium,
    alignSelf: 'flex-start',
  },
  buttonDisabled: {
    backgroundColor: colors.gray200,
    color: colors.textSecondary,
    border: `1px solid ${colors.border}`,
    cursor: 'not-allowed',
  },
  linkButton: {
    background: 'none',
    border: 'none',
    padding: 0,
    color: colors.primary,
    fontSize: typography.small.fontSize,
    cursor: 'pointer',
    textDecoration: 'underline',
    fontFamily: 'inherit',
    textAlign: 'left',
  },
  mono: {
    fontFamily: 'monospace',
    fontSize: typography.caption.fontSize,
  },
  error: {
    color: colors.error,
    fontSize: typography.small.fontSize,
    marginTop: spacing.sm,
    marginBottom: spacing.sm,
  },
  shipForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.sm,
    alignItems: 'flex-start',
  },
  inputLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
    fontSize: typography.small.fontSize,
    color: colors.textSecondary,
    width: '100%',
  },
  input: {
    padding: `${spacing.xs} ${spacing.sm}`,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    fontSize: typography.small.fontSize,
    color: colors.text,
    backgroundColor: colors.white,
  },
  resultBox: {
    marginTop: spacing.md,
    padding: spacing.md,
    borderRadius: borderRadius.md,
    backgroundColor: `${colors.success}14`,
    border: `1px solid ${colors.success}`,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.sm,
  },
  resultTitle: {
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: colors.text,
  },
  preserved: {
    margin: 0,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: colors.text,
  },
};
